/*
  Inner Botanica – Low-Tox Home Audit: application logic.
  Content/weights live in tox-audit-data.js (window.IB_TA_DATA).
  Settings/asset URLs come from the section (window.IB_TA_CONFIG).

  Privacy contract: answers live in memory + sessionStorage only. They are
  never transmitted. Analytics carry event names + room ids only — never
  answer content. All rendering uses textContent (never innerHTML).

  Scoring: see the model note at the top of tox-audit-data.js. Tier-0
  ("peace of mind") chips never touch the score.
*/
(function () {
  "use strict";

  var DATA = window.IB_TA_DATA;
  var CONFIG = window.IB_TA_CONFIG || {};
  var S = DATA.strings;
  var STORE_KEY = "ib_ta_state_v1";

  /* ------------------------------------------------------------ lookups */
  var ROOM_BY_ID = {};
  var EXPOSURE = {}; /* chip id -> { chip, room } */
  var CREDIT = {};   /* chip id -> { chip, room } */
  DATA.rooms.forEach(function (room) {
    ROOM_BY_ID[room.id] = room;
    room.exposures.forEach(function (c) { EXPOSURE[c.id] = { chip: c, room: room }; });
    room.credits.forEach(function (c) { CREDIT[c.id] = { chip: c, room: room }; });
  });

  /* -------------------------------------------------------------- state */
  function freshAnswers() {
    return { v: 1, rooms: {} };
  }
  function roomRec(answers, roomId) {
    if (!answers.rooms[roomId]) answers.rooms[roomId] = { e: [], c: [], s: 0 };
    return answers.rooms[roomId];
  }
  var state = {
    answers: freshAnswers(),
    screen: "intro",
    history: [],
    fromShare: false
  };

  /* ------------------------------------------------------------ persist */
  var memStore = null;
  function save() {
    var payload = JSON.stringify({
      answers: state.answers,
      screen: state.screen,
      history: state.history
    });
    try { sessionStorage.setItem(STORE_KEY, payload); }
    catch (e) { memStore = payload; }
  }
  function load() {
    var raw = null;
    try { raw = sessionStorage.getItem(STORE_KEY); } catch (e) { raw = memStore; }
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (parsed && parsed.answers && parsed.answers.v === 1) return parsed;
    } catch (e) { /* corrupt — ignore */ }
    return null;
  }
  function clearSaved() {
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* noop */ }
    memStore = null;
  }

  /* ---------------------------------------------------------- analytics
     Room/step ids only. Never answer content, never scores. */
  function track(event, stepId) {
    try {
      var payload = { event: event };
      if (stepId) payload.step_id = stepId;
      (window.dataLayer = window.dataLayer || []).push(payload);
      document.dispatchEvent(new CustomEvent("funnelocity:tox-event", {
        detail: { event: event, step_id: stepId || null }
      }));
    } catch (e) { /* analytics must never break the flow */ }
  }

  /* --------------------------------------------------------- dom helpers */
  function byId(id) { return document.getElementById(id); }
  function h(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }
  function btn(cls, label, onClick) {
    var b = h("button", cls, label);
    b.type = "button";
    b.addEventListener("click", onClick);
    return b;
  }
  function chipButton(label, pressed, onToggle) {
    var c = btn("ib-ta-chip", label, function () { onToggle(c); });
    c.setAttribute("aria-pressed", pressed ? "true" : "false");
    if (pressed) c.classList.add("is-on");
    return c;
  }
  function setPressed(node, on) {
    node.setAttribute("aria-pressed", on ? "true" : "false");
    node.classList.toggle("is-on", !!on);
  }

  /* ================================================================ scoring
     Pure: answers -> { overall, rooms: [...] }. `exclude` is an optional
     { chipId: true } map of exposures treated as swapped (for projection). */
  function computeScore(answers, exclude) {
    var sumEarned = 0, sumAvail = 0;
    var rooms = DATA.rooms.map(function (room) {
      var rec = answers.rooms[room.id];
      var answered = !!rec && !rec.s;
      var max = 0, cmax = 0;
      room.exposures.forEach(function (c) { max += c.tier; });
      room.credits.forEach(function (c) { cmax += c.pts; });
      if (!answered || max + cmax === 0) {
        return { id: room.id, title: room.title, answered: false, score: null };
      }
      var burden = 0, credit = 0;
      rec.e.forEach(function (id) {
        if (exclude && exclude[id]) return;
        var hit = EXPOSURE[id];
        if (hit && hit.room.id === room.id) burden += hit.chip.tier;
      });
      rec.c.forEach(function (id) {
        var hit = CREDIT[id];
        if (hit && hit.room.id === room.id) credit += hit.chip.pts;
      });
      var earned = (max - burden) + credit;
      var avail = max + cmax;
      sumEarned += earned;
      sumAvail += avail;
      return { id: room.id, title: room.title, answered: true, score: Math.round((earned / avail) * 100) };
    });
    return {
      overall: sumAvail ? Math.round((sumEarned / sumAvail) * 100) : null,
      rooms: rooms
    };
  }

  /* The plan: top five swaps ranked by tier/(effort+1), the rest grouped by
     effort, unadopted credits as "start" suggestions, tier-0 as peace items. */
  function buildPlan(answers) {
    var candidates = [], peace = [], creditsHeld = [], laterStarts = [];
    DATA.rooms.forEach(function (room) {
      var rec = answers.rooms[room.id];
      if (!rec || rec.s) return;
      rec.e.forEach(function (id) {
        var hit = EXPOSURE[id];
        if (!hit || hit.room.id !== room.id) return;
        if (hit.chip.tier === 0) peace.push({ chip: hit.chip, room: room });
        else candidates.push({ chip: hit.chip, room: room, value: hit.chip.tier / (hit.chip.effort + 1) });
      });
      room.credits.forEach(function (c) {
        if (rec.c.indexOf(c.id) !== -1) creditsHeld.push({ chip: c, room: room });
        else laterStarts.push({ chip: c, room: room, start: true });
      });
    });
    candidates.sort(function (a, b) {
      return (b.value - a.value) || (b.chip.tier - a.chip.tier) || 0;
    });
    /* Top five, max two per room so the plan spans the home; backfill from
       the remainder (still value-sorted) if the cap leaves slots open. */
    var top = [], rest = [], perRoom = {};
    candidates.forEach(function (it) {
      var n = perRoom[it.room.id] || 0;
      if (top.length < 5 && n < 2) { top.push(it); perRoom[it.room.id] = n + 1; }
      else rest.push(it);
    });
    while (top.length < 5 && rest.length) top.push(rest.shift());
    top.sort(function (a, b) {
      return (b.value - a.value) || (b.chip.tier - a.chip.tier) || 0;
    });

    /* Credit suggestions share titles with their paired exposure swap — drop
       any "start" suggestion whose advice is already on the plan. */
    var usedTitles = {};
    top.forEach(function (it) { usedTitles[it.chip.title] = true; });
    rest.forEach(function (it) { usedTitles[it.chip.title] = true; });
    laterStarts = laterStarts.filter(function (it) { return !usedTitles[it.chip.title]; });

    var later = { 0: [], 1: [], 2: [] };
    rest.forEach(function (it) { later[it.chip.effort].push(it); });
    laterStarts.forEach(function (it) { later[it.chip.effort].push(it); });

    var score = computeScore(answers, null);
    var exclude = {};
    top.forEach(function (it) { exclude[it.chip.id] = true; });
    var projected = top.length ? computeScore(answers, exclude).overall : score.overall;

    return {
      score: score,
      projected: projected,
      top: top,
      later: later,
      peace: peace,
      credits: creditsHeld
    };
  }

  /* ----------------------------------------------------------------- path */
  function buildPath() {
    return DATA.rooms.map(function (room) { return "r:" + room.id; });
  }
  function nextKey(afterKey) {
    var p = buildPath();
    var i = p.indexOf(afterKey);
    return i === -1 || i + 1 >= p.length ? null : p[i + 1];
  }

  /* --------------------------------------------------------- view states */
  var VIEW_IDS = ["ib-ta-intro", "ib-ta-interview", "ib-ta-transition", "ib-ta-results", "ib-ta-after"];
  function showView(id) {
    VIEW_IDS.forEach(function (v) {
      var node = byId(v);
      if (node) node.classList.toggle("is-active", v === id);
    });
    var progress = byId("ib-ta-progress");
    if (progress) progress.hidden = id !== "ib-ta-interview";
    window.scrollTo(0, 0);
  }

  /* --------------------------------------------------------------- router */
  function go(key, opts) {
    opts = opts || {};
    var roomId = key.split(":")[1];
    var room = ROOM_BY_ID[roomId];
    if (!room) return;
    if (!opts.silent) {
      if (state.screen !== key) state.history.push(state.screen);
      state.screen = key;
    }
    showView("ib-ta-interview");
    renderRoom(room);
    updateProgress(key);
    track("tox_step", "r_" + roomId);
    save();
  }
  function goBack() {
    var prev = state.history.pop();
    if (!prev || prev === "intro") {
      state.screen = "intro";
      showView("ib-ta-intro");
      save();
      return;
    }
    state.screen = prev;
    var room = ROOM_BY_ID[prev.split(":")[1]];
    if (!room) { showView("ib-ta-intro"); return; }
    showView("ib-ta-interview");
    renderRoom(room);
    updateProgress(prev);
    save();
  }
  function advance(fromKey) {
    var nk = nextKey(fromKey);
    if (nk) { go(nk); return; }
    track("tox_rooms_complete");
    reveal();
  }
  function updateProgress(key) {
    var p = buildPath();
    var i = p.indexOf(key);
    var fill = byId("ib-ta-progress-fill");
    if (fill && i !== -1) fill.style.width = Math.round(((i + 1) / (p.length + 1)) * 100) + "%";
  }

  /* ---------------------------------------------------------- room screen */
  function renderRoom(room) {
    var rec = roomRec(state.answers, room.id);
    var mount = byId("ib-ta-screen");
    mount.textContent = "";
    var card = h("div", "ib-ta-card");
    var head = h("h2", "ib-ta-card__prompt", room.prompt);
    head.tabIndex = -1;
    card.appendChild(head);
    card.appendChild(h("p", "ib-ta-card__sub", room.sub));

    function toggleIn(list, id, node) {
      var i = list.indexOf(id);
      if (i === -1) list.push(id); else list.splice(i, 1);
      rec.s = 0; /* any interaction un-skips the room */
      setPressed(node, list.indexOf(id) !== -1);
      save();
    }

    card.appendChild(h("p", "ib-ta-group-label", S.exposuresLabel));
    var eWrap = h("div", "ib-ta-chips");
    room.exposures.forEach(function (chip) {
      eWrap.appendChild(chipButton(chip.label, rec.e.indexOf(chip.id) !== -1, function (node) {
        toggleIn(rec.e, chip.id, node);
      }));
    });
    card.appendChild(eWrap);

    card.appendChild(h("p", "ib-ta-group-label ib-ta-group-label--credit", S.creditsLabel));
    var cWrap = h("div", "ib-ta-chips ib-ta-chips--credit");
    room.credits.forEach(function (chip) {
      cWrap.appendChild(chipButton(chip.label, rec.c.indexOf(chip.id) !== -1, function (node) {
        toggleIn(rec.c, chip.id, node);
      }));
    });
    card.appendChild(cWrap);

    var foot = h("div", "ib-ta-card__footer");
    foot.appendChild(btn("ib-ta-btn ib-ta-btn--ghost", S.back, goBack));
    var right = h("div", "ib-ta-card__footer-right");
    right.appendChild(btn("ib-ta-btn ib-ta-btn--link", S.skipRoom, function () {
      rec.e = [];
      rec.c = [];
      rec.s = 1;
      save();
      track("tox_room_skip", "r_" + room.id);
      advance("r:" + room.id);
    }));
    right.appendChild(btn("ib-ta-btn ib-ta-btn--primary", S.next, function () {
      rec.s = 0; /* pressing Next means "answered", even with nothing tapped */
      save();
      advance("r:" + room.id);
    }));
    foot.appendChild(right);
    card.appendChild(foot);

    mount.appendChild(card);
    window.requestAnimationFrame(function () { head.focus(); });
  }

  /* ============================================================== results */
  var currentPlan = null;

  function effortTag(effort) { return DATA.effortLabels[effort] || ""; }

  function renderResults(plan) {
    var root = byId("ib-ta-plan");
    root.textContent = "";

    /* score block */
    var scoreBlock = h("div", "ib-ta-score");
    scoreBlock.appendChild(h("p", "ib-ta-score__label", S.results.scoreLabel));
    scoreBlock.appendChild(h("p", "ib-ta-score__number", plan.score.overall === null ? "—" : String(plan.score.overall)));
    if (plan.score.overall === null) {
      scoreBlock.appendChild(h("p", "ib-ta-score__projection", S.results.emptyNote));
    } else if (plan.top.length && plan.projected > plan.score.overall) {
      scoreBlock.appendChild(h("p", "ib-ta-score__projection",
        S.results.projectedTpl.replace("{n}", String(plan.projected))));
    }
    root.appendChild(scoreBlock);

    /* room bars */
    if (plan.score.overall !== null) {
      var roomsSec = h("section", "ib-ta-rooms");
      roomsSec.appendChild(h("h3", "ib-ta-section-heading", S.results.roomsHeading));
      var anySkipped = false;
      plan.score.rooms.forEach(function (r) {
        var row = h("div", "ib-ta-room-row");
        row.appendChild(h("span", "ib-ta-room-row__name", r.title));
        if (r.answered) {
          var barWrap = h("span", "ib-ta-room-row__bar");
          var fill = h("span", "ib-ta-room-row__fill");
          fill.style.width = r.score + "%";
          barWrap.appendChild(fill);
          row.appendChild(barWrap);
          row.appendChild(h("span", "ib-ta-room-row__score", String(r.score)));
        } else {
          anySkipped = true;
          row.appendChild(h("span", "ib-ta-room-row__skipped", "Skipped"));
        }
        roomsSec.appendChild(row);
      });
      if (anySkipped) roomsSec.appendChild(h("p", "ib-ta-note", S.results.skippedNote));
      root.appendChild(roomsSec);
    }

    /* credits */
    if (plan.credits.length) {
      var credSec = h("section", "ib-ta-credits");
      credSec.appendChild(h("h3", "ib-ta-section-heading", S.results.creditsHeading));
      credSec.appendChild(h("p", "ib-ta-section-sub", S.results.creditsSub));
      var ul = h("ul", "ib-ta-credit-list");
      plan.credits.forEach(function (it) {
        ul.appendChild(h("li", null, it.chip.label));
      });
      credSec.appendChild(ul);
      root.appendChild(credSec);
    }

    /* top five */
    if (plan.top.length) {
      var topSec = h("section", "ib-ta-top");
      topSec.appendChild(h("h3", "ib-ta-section-heading", S.results.topHeading));
      topSec.appendChild(h("p", "ib-ta-section-sub", S.results.topSub));
      plan.top.forEach(function (it, i) {
        var cardEl = h("div", "ib-ta-swap-card");
        var headRow = h("div", "ib-ta-swap-card__head");
        headRow.appendChild(h("span", "ib-ta-swap-card__num", String(i + 1)));
        headRow.appendChild(h("h4", "ib-ta-swap-card__title", it.chip.title));
        cardEl.appendChild(headRow);
        cardEl.appendChild(h("p", "ib-ta-swap-card__body", it.chip.swap));
        var meta = h("p", "ib-ta-swap-card__meta");
        meta.appendChild(h("span", "ib-ta-tag ib-ta-tag--effort" + it.chip.effort, effortTag(it.chip.effort)));
        meta.appendChild(h("span", "ib-ta-swap-card__room", it.room.title));
        cardEl.appendChild(meta);
        topSec.appendChild(cardEl);
      });
      root.appendChild(topSec);
    }

    /* when you're ready */
    var laterCount = plan.later[0].length + plan.later[1].length + plan.later[2].length;
    if (laterCount) {
      var laterSec = h("section", "ib-ta-later");
      laterSec.appendChild(h("h3", "ib-ta-section-heading", S.results.laterHeading));
      laterSec.appendChild(h("p", "ib-ta-section-sub", S.results.laterSub));
      [0, 1, 2].forEach(function (effort) {
        var items = plan.later[effort];
        if (!items.length) return;
        var det = document.createElement("details");
        det.className = "ib-ta-level";
        var sum = document.createElement("summary");
        sum.className = "ib-ta-level__summary";
        sum.textContent = effortTag(effort) + " (" + items.length + ")";
        det.appendChild(sum);
        var lul = h("ul", "ib-ta-level__list");
        items.forEach(function (it) {
          var li = h("li", null, (it.start ? S.results.startPrefix : "") + it.chip.title);
          li.appendChild(h("span", "ib-ta-level__room", " — " + it.room.title));
          lul.appendChild(li);
        });
        det.appendChild(lul);
        laterSec.appendChild(det);
      });
      root.appendChild(laterSec);
    }

    /* peace of mind */
    if (plan.peace.length) {
      var peaceSec = h("section", "ib-ta-peace");
      peaceSec.appendChild(h("h3", "ib-ta-section-heading", S.results.peaceHeading));
      peaceSec.appendChild(h("p", "ib-ta-section-sub", S.results.peaceSub));
      plan.peace.forEach(function (it) {
        var p = h("div", "ib-ta-peace__item");
        p.appendChild(h("h4", "ib-ta-peace__title", it.chip.title));
        p.appendChild(h("p", "ib-ta-peace__body", it.chip.swap));
        peaceSec.appendChild(p);
      });
      root.appendChild(peaceSec);
    }

    /* supplements note */
    var supps = h("section", "ib-ta-supps");
    supps.appendChild(h("h3", "ib-ta-section-heading", S.results.suppsHeading));
    supps.appendChild(h("p", "ib-ta-supps__body", S.results.suppsBody));
    root.appendChild(supps);

    root.appendChild(h("p", "ib-ta-note ib-ta-note--retake", S.results.retakeNote));
  }

  function reveal() {
    showView("ib-ta-transition");
    state.screen = "results";
    save();
    window.setTimeout(function () {
      currentPlan = buildPlan(state.answers);
      renderResults(currentPlan);
      showView("ib-ta-results");
      track("tox_results_view");
      pdf.prepare();
    }, prefersReducedMotion() ? 0 : 1100);
  }
  function prefersReducedMotion() {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
    catch (e) { return false; }
  }

  /* ================================================================== PDF */
  var pdf = (function () {
    var readyPromise = null;
    var fonts = { regular: null, bold: null, failed: false };
    var plan = null; /* fit plan: { step } */

    function loadScript(src) {
      return new Promise(function (resolve, reject) {
        if (window.jspdf) { resolve(); return; }
        var sc = document.createElement("script");
        sc.src = src;
        sc.onload = resolve;
        sc.onerror = reject;
        document.head.appendChild(sc);
      });
    }
    function fetchB64(url) {
      return fetch(url).then(function (r) {
        if (!r.ok) throw new Error("font fetch failed");
        return r.arrayBuffer();
      }).then(function (buf) {
        var bytes = new Uint8Array(buf);
        var bin = "";
        var CHUNK = 0x8000;
        for (var i = 0; i < bytes.length; i += CHUNK) {
          bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
        }
        return btoa(bin);
      });
    }
    function ensureReady() {
      if (!readyPromise) {
        readyPromise = loadScript(CONFIG.assets.jspdf).then(function () {
          return Promise.all([fetchB64(CONFIG.assets.fontRegular), fetchB64(CONFIG.assets.fontBold)])
            .then(function (res) { fonts.regular = res[0]; fonts.bold = res[1]; })
            .catch(function () {
              fonts.failed = true;
              track("tox_pdf_font_fallback");
            });
        });
      }
      return readyPromise;
    }
    function newDoc() {
      var doc = new window.jspdf.jsPDF({ unit: "pt", format: "letter" });
      if (!fonts.failed && fonts.regular && fonts.bold) {
        doc.addFileToVFS("DMSans-Regular.ttf", fonts.regular);
        doc.addFont("DMSans-Regular.ttf", "DMSans", "normal");
        doc.addFileToVFS("DMSans-Bold.ttf", fonts.bold);
        doc.addFont("DMSans-Bold.ttf", "DMSans", "bold");
      }
      return doc;
    }
    function fontName() { return fonts.failed || !fonts.regular ? "helvetica" : "DMSans"; }

    var PAGE = { w: 612, h: 792, margin: 44 };
    var INK = [27, 34, 37];
    var SLATE = [57, 91, 97];
    var GREEN = [120, 157, 44];

    function firstSentence(str) {
      var i = str.indexOf(". ");
      return i === -1 ? str : str.slice(0, i + 1);
    }

    /* Steps: 0 full; 1 swap bodies -> first sentence; 2 drop swap bodies;
       3 smaller body font. */
    function drawPlan(doc, p, step, dry) {
      var body = step >= 3 ? 8.5 : 9.5;
      var lh = body * 1.35;
      var gap = step >= 3 ? 10 : 13;
      var x = PAGE.margin;
      var w = PAGE.w - PAGE.margin * 2;
      var y = PAGE.margin + 12;
      var footerY = PAGE.h - PAGE.margin;
      var f = fontName();

      function text(str, size, style, color, tx, maxW, lineH) {
        var lines = doc.splitTextToSize(str, maxW || w);
        doc.setFont(f, style || "normal");
        doc.setFontSize(size);
        if (!dry) {
          doc.setTextColor(color[0], color[1], color[2]);
          doc.text(lines, tx || x, y);
        }
        y += lines.length * (lineH || size * 1.35);
      }
      function heading(str) {
        if (!dry && doc.setCharSpace) doc.setCharSpace(0.5);
        text(str.toUpperCase(), step >= 3 ? 9.5 : 10.5, "bold", INK, x, w, (step >= 3 ? 9.5 : 10.5) * 1.2);
        if (!dry && doc.setCharSpace) doc.setCharSpace(0);
        y += 2;
      }

      /* header band */
      doc.setFont(f, "bold"); doc.setFontSize(16);
      if (!dry) {
        doc.setTextColor(INK[0], INK[1], INK[2]);
        doc.text(S.pdf.title, x, y);
        doc.setFont(f, "normal"); doc.setFontSize(9);
        doc.setTextColor(SLATE[0], SLATE[1], SLATE[2]);
        doc.text(S.pdf.preparedPrefix + new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }), PAGE.w - PAGE.margin, y, { align: "right" });
      }
      y += 16;
      if (!dry) {
        doc.setDrawColor(GREEN[0], GREEN[1], GREEN[2]);
        doc.setLineWidth(1.2);
        doc.line(x, y, PAGE.w - PAGE.margin, y);
      }
      y += gap + 4;

      /* score line */
      if (p.score.overall !== null) {
        var scoreStr = (p.top.length && p.projected > p.score.overall)
          ? S.pdf.scoreTpl.replace("{now}", String(p.score.overall)).replace("{next}", String(p.projected))
          : S.pdf.scoreOnlyTpl.replace("{now}", String(p.score.overall));
        text(scoreStr, body + 1, "bold", INK, x, w, (body + 1) * 1.4);
        y += gap;
      }

      /* top five */
      if (p.top.length) {
        heading(S.pdf.topHeading);
        p.top.forEach(function (it, i) {
          text((i + 1) + ". " + it.chip.title + "  (" + DATA.effortLabels[it.chip.effort] + ")", body, "bold", INK, x + 4, w - 8, lh);
          if (step < 2) {
            var bodyStr = step === 1 ? firstSentence(it.chip.swap) : it.chip.swap;
            text(bodyStr, body - 0.5, "normal", SLATE, x + 16, w - 24, (body - 0.5) * 1.35);
          }
          y += 2.5;
        });
        y += gap - 4;
      }

      /* keep doing */
      if (p.credits.length) {
        heading(S.pdf.creditsHeading);
        text(p.credits.map(function (it) { return it.chip.label; }).join(" · "), body, "normal", INK, x + 4, w - 8, lh);
        y += gap;
      }

      /* when you're ready */
      var laterCount = p.later[0].length + p.later[1].length + p.later[2].length;
      if (laterCount) {
        heading(S.pdf.laterHeading);
        var prefixes = [S.pdf.laterFree, S.pdf.laterCheap, S.pdf.laterInvest];
        [0, 1, 2].forEach(function (effort) {
          var items = p.later[effort];
          if (!items.length) return;
          text(prefixes[effort] + items.map(function (it) {
            return (it.start ? S.results.startPrefix : "") + it.chip.title;
          }).join(" · "), body, "normal", INK, x + 4, w - 8, lh);
          y += 1.5;
        });
        y += gap - 2;
      }

      /* peace of mind */
      if (p.peace.length) {
        heading(S.pdf.peaceHeading);
        text(p.peace.map(function (it) { return it.chip.title; }).join(" · "), body, "normal", INK, x + 4, w - 8, lh);
        y += gap;
      }

      /* footer pinned to bottom */
      if (!dry) {
        doc.setFont(f, "normal"); doc.setFontSize(7.5);
        doc.setTextColor(SLATE[0], SLATE[1], SLATE[2]);
        doc.text(S.pdf.footerCredit, x, footerY - 10);
        var disc = doc.splitTextToSize(S.pdf.footerDisclaimer, w);
        doc.text(disc, x, footerY);
      }
      return y;
    }

    var FOOTER_CLEAR = 34;
    function computeFit(p) {
      var doc = newDoc(); /* scratch doc for measurement */
      for (var step = 0; step <= 3; step++) {
        var endY = drawPlan(doc, p, step, true);
        if (endY <= PAGE.h - PAGE.margin - FOOTER_CLEAR) return { step: step };
      }
      return { step: 3 };
    }

    function prepare() {
      ensureReady().then(function () {
        if (!window.jspdf || !currentPlan) return;
        try { plan = computeFit(currentPlan); } catch (e) { plan = { step: 0 }; }
      }).catch(function () { /* handled at download time */ });
    }

    function isConstrainedBrowser() {
      var ua = navigator.userAgent || "";
      var iOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      var inApp = /Instagram|FBAN|FBAV|FB_IAB|Line\/|MicroMessenger/.test(ua);
      return { iOS: iOS, inApp: inApp };
    }

    function download() {
      var help = byId("ib-ta-download-help");
      return ensureReady().then(function () {
        if (!window.jspdf) throw new Error("jspdf unavailable");
        if (!plan) plan = computeFit(currentPlan);
        var doc = newDoc();
        drawPlan(doc, currentPlan, plan.step, false);
        var stamp = new Date().toISOString().slice(0, 10);
        var name = "Low-Tox-Plan-" + stamp + ".pdf";
        var env = isConstrainedBrowser();
        if (env.iOS || env.inApp) {
          var blob = doc.output("blob");
          var url = URL.createObjectURL(blob);
          var a = document.createElement("a");
          a.href = url;
          a.download = name;
          a.target = "_blank";
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.setTimeout(function () { URL.revokeObjectURL(url); }, 60000);
          if (help) { help.textContent = env.inApp ? S.download.blocked : S.download.iosHint; help.hidden = false; }
        } else {
          doc.save(name);
        }
        track("tox_pdf_download");
        return true;
      }).catch(function () {
        if (help) { help.textContent = S.download.blocked; help.hidden = false; }
        return false;
      });
    }

    return { prepare: prepare, download: download };
  })();

  /* ================================================================ share */
  function shareUrl() {
    var payload = window.LZString.compressToEncodedURIComponent(JSON.stringify(state.answers));
    var url = location.origin + location.pathname + "?k=" + payload;
    if (url.length > 1800) track("tox_share_long_url");
    return url;
  }
  function doShare() {
    var url = shareUrl();
    var done = function () { track("tox_share"); showAfter(); };
    if (navigator.share) {
      navigator.share({ title: S.share.shareTitle, url: url }).then(done).catch(function () { /* cancelled */ });
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function () {
        flashShareNote(S.share.copied);
        done();
      }).catch(function () { manualCopy(url); done(); });
    } else {
      manualCopy(url);
      done();
    }
  }
  function flashShareNote(msg) {
    var help = byId("ib-ta-download-help");
    if (help) { help.textContent = msg; help.hidden = false; }
  }
  function manualCopy(url) {
    var help = byId("ib-ta-download-help");
    if (!help) return;
    help.textContent = "";
    help.appendChild(h("p", null, S.share.copyFail));
    var input = h("input", "ib-ta-input");
    input.type = "text";
    input.readOnly = true;
    input.value = url;
    input.addEventListener("focus", function () { input.select(); });
    help.appendChild(input);
    help.hidden = false;
  }

  /* ====================================================== post-download */
  function stabilityHits(answers) {
    var count = 0;
    DATA.rooms.forEach(function (room) {
      var rec = answers.rooms[room.id];
      if (!rec || rec.s) return;
      rec.e.forEach(function (id) {
        if (DATA.products.stabilityTerritory.indexOf(id) !== -1) count++;
      });
    });
    return count;
  }
  function showAfter() {
    var root = byId("ib-ta-after");
    root.textContent = "";
    var wrap = h("div", "ib-ta-after");
    wrap.appendChild(h("h2", "ib-ta-after__head", S.after.head));
    wrap.appendChild(h("p", "ib-ta-after__body", S.after.body));

    if (stabilityHits(state.answers) >= DATA.products.minHits) {
      var card = DATA.products.card;
      var url = CONFIG[card.urlKey] || "";
      var cardEl = h("div", "ib-ta-product-card");
      cardEl.appendChild(h("p", "ib-ta-product-card__body", card.body));
      if (url) {
        var link = h("a", "ib-ta-product-card__link", card.link);
        link.href = url;
        link.addEventListener("click", function () { track("tox_stability_card_click"); });
        cardEl.appendChild(link);
      }
      wrap.appendChild(cardEl);
      wrap.appendChild(h("p", "ib-ta-fda", DATA.products.fdaDisclaimer));
      track("tox_stability_card_view");
    }

    var links = h("div", "ib-ta-after__links");
    links.appendChild(btn("ib-ta-btn ib-ta-btn--secondary", S.after.downloadAgain, function () {
      showView("ib-ta-results");
    }));
    links.appendChild(btn("ib-ta-btn ib-ta-btn--link", S.after.friend, function () {
      track("tox_retake");
      clearSaved();
      state.answers = freshAnswers();
      state.history = [];
      state.screen = "intro";
      var help = byId("ib-ta-download-help");
      if (help) help.hidden = true;
      showView("ib-ta-intro");
    }));
    wrap.appendChild(links);
    root.appendChild(wrap);
    showView("ib-ta-after");
  }

  /* ============================================================== decode */
  function sanitizeAnswers(raw) {
    if (!raw || typeof raw !== "object" || raw.v !== 1) return null;
    if (!raw.rooms || typeof raw.rooms !== "object") return null;
    var a = freshAnswers();
    var any = false;
    DATA.rooms.forEach(function (room) {
      var rec = raw.rooms[room.id];
      if (!rec || typeof rec !== "object") return;
      var clean = {
        e: Array.isArray(rec.e) ? rec.e.filter(function (id) {
          return EXPOSURE[id] && EXPOSURE[id].room.id === room.id;
        }) : [],
        c: Array.isArray(rec.c) ? rec.c.filter(function (id) {
          return CREDIT[id] && CREDIT[id].room.id === room.id;
        }) : [],
        s: rec.s ? 1 : 0
      };
      a.rooms[room.id] = clean;
      any = true;
    });
    return any ? a : null;
  }
  function tryDecodeShareLink() {
    var match = /[?&]k=([^&]+)/.exec(location.search);
    if (!match) return false;
    var answers = null;
    try {
      var json = window.LZString.decompressFromEncodedURIComponent(match[1]);
      answers = sanitizeAnswers(JSON.parse(json));
    } catch (e) { answers = null; }
    if (!answers) {
      try { history.replaceState(null, "", location.pathname); } catch (e) { /* noop */ }
      return false;
    }
    state.answers = answers;
    state.fromShare = true;
    state.screen = "results";
    currentPlan = buildPlan(answers);
    renderResults(currentPlan);
    showView("ib-ta-results");
    track("tox_results_view");
    track("tox_step", "shared_plan");
    pdf.prepare();
    return true;
  }

  /* ---------------------------------------------------------- dev hooks */
  function mockAnswers() {
    var a = freshAnswers();
    a.rooms = {
      kt: { e: ["kt_sugar", "kt_alcohol", "kt_mw_plastic", "kt_storage", "kt_canned", "kt_caffeine", "kt_big_fish"], c: ["kt_c_cook"], s: 0 },
      wa: { e: ["wa_tap", "wa_hot_bottle", "wa_shower"], c: [], s: 0 },
      bb: { e: ["bb_fragrance", "bb_dye", "bb_period", "bb_alum"], c: ["bb_c_mineral"], s: 0 },
      bd: { e: ["bd_phone", "bd_screens", "bd_light", "bd_router"], c: [], s: 0 },
      ln: { e: ["ln_dryer", "ln_gasket", "ln_antibac"], c: [], s: 0 },
      ah: { e: ["ah_fresheners", "ah_gas", "ah_shoes", "ah_dust", "ah_radon"], c: [], s: 0 },
      dg: { e: ["dg_first", "dg_doom", "dg_notif", "dg_rabbit", "dg_emf"], c: ["dg_c_offline"], s: 0 }
    };
    return a;
  }
  function tryDevHooks() {
    var mock = /[?&]ta_mock=full/.test(location.search);
    var screenMatch = /[?&]ta_screen=r_([a-z]+)/.exec(location.search);
    if (!mock && !screenMatch) return false;
    state.answers = mockAnswers();
    if (mock) {
      state.screen = "results";
      currentPlan = buildPlan(state.answers);
      renderResults(currentPlan);
      showView("ib-ta-results");
      pdf.prepare();
      return true;
    }
    go("r:" + screenMatch[1]);
    return true;
  }

  /* ================================================================ boot */
  function wireStaticActions(root) {
    root.addEventListener("click", function (ev) {
      var target = ev.target.closest ? ev.target.closest("[data-ta-action]") : null;
      if (!target) return;
      var action = target.getAttribute("data-ta-action");
      if (action === "start") {
        track("tox_start");
        var saved = load();
        if (saved && saved.screen && saved.screen !== "intro") {
          state.answers = saved.answers;
          state.history = saved.history || [];
          if (saved.screen === "results") { reveal(); return; }
          go(saved.screen, { silent: false });
        } else {
          go("r:" + DATA.rooms[0].id);
        }
      } else if (action === "download") {
        pdf.download().then(function (ok) { if (ok) showAfter(); });
      } else if (action === "share") {
        doShare();
      } else if (action === "edit") {
        track("tox_edit_answers");
        state.history = [];
        go("r:" + DATA.rooms[0].id);
      } else if (action === "print") {
        document.documentElement.classList.add("ib-ta-printing");
        window.setTimeout(function () {
          window.print();
          document.documentElement.classList.remove("ib-ta-printing");
        }, 50);
      }
    });
  }

  var booted = false;
  function init() {
    var root = document.getElementById("ib-ta");
    if (!root || booted) return;
    booted = true;
    wireStaticActions(root);
    if (tryDevHooks()) return;
    if (tryDecodeShareLink()) return;
    showView("ib-ta-intro");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  /* Shopify theme editor reloads sections without a page load */
  document.addEventListener("shopify:section:load", function () {
    booted = false;
    init();
  });
})();
